import { create } from 'zustand';

import type { TransferSource } from 'app/(trade)/components/TransferBalance/queries';
import {
    getEffectiveDepositDestination,
    getFundingAccountStatus,
    resolveEffectiveDepositDestination,
    type FundingAccountPolicyResult,
} from 'components/Transfer/fundingAccountPolicy';
import { warmPlaidProtectSession } from 'lib/plaidProtect';
import { useAuthStore } from 'lib/stores/authStore';
import type { DepositDestination, WithdrawalSource } from 'models/reqres';
import { invalidateAllBalanceQueries } from 'utils/invalidateBalanceQueries';

export type TransferTab = 'deposit' | 'withdraw' | 'history' | 'transfer';

export function toDepositDestination(source: TransferSource): DepositDestination {
    return getEffectiveDepositDestination(source);
}

export function resolveDepositDestination(source: TransferSource): FundingAccountPolicyResult<DepositDestination> {
    return resolveEffectiveDepositDestination(source);
}

const TRANSFER_SOURCE_TO_WITHDRAWAL_SOURCE: Record<TransferSource, WithdrawalSource> = {
    event_contract: 'event_contract',
    margined: 'margin',
};

export function toWithdrawalSource(source: TransferSource): WithdrawalSource {
    return TRANSFER_SOURCE_TO_WITHDRAWAL_SOURCE[source];
}

export type TransferDialogBeforeOpenResult = 'continue' | 'blocked';
type TransferDialogBeforeOpenGuard = () => TransferDialogBeforeOpenResult;

let beforeOpenGuard: TransferDialogBeforeOpenGuard | null = null;

export function registerTransferDialogBeforeOpenGuard(guard: TransferDialogBeforeOpenGuard): () => void {
    beforeOpenGuard = guard;

    return () => {
        if (beforeOpenGuard === guard) {
            beforeOpenGuard = null;
        }
    };
}

export function clearTransferDialogBeforeOpenGuard(): void {
    beforeOpenGuard = null;
}

function shouldOpenDeposit(): boolean {
    return beforeOpenGuard?.() !== 'blocked';
}

interface TransferDialogState {
    isOpen: boolean;
    activeTab: TransferTab;
    depositDestination: TransferSource;
    isSelectingDepositDestination: boolean;
    isSelectingDepositSource: boolean;
    withdrawalSource: TransferSource;
    isSelectingWithdrawalSource: boolean;
    depositAutoSelectFired: boolean;
    actions: {
        depositOpened: () => boolean;
        depositOpenedUnchecked: () => void;
        withdrawOpened: () => void;
        transferOpened: () => void;
        tabSwitched: (tab: TransferTab) => void;
        dialogClosed: () => void;
        depositAutoSelectDidFire: () => void;
        depositDestinationPickerOpened: () => void;
        depositDestinationPickerClosed: () => void;
        depositDestinationSelected: (destination: TransferSource) => void;
        depositSourcePickerOpened: () => void;
        depositSourcePickerClosed: () => void;
        withdrawalSourcePickerOpened: () => void;
        withdrawalSourcePickerClosed: () => void;
        withdrawalSourceSelected: (source: TransferSource) => void;
    };
}

const OPEN_DEPOSIT_STATE = {
    isOpen: true,
    activeTab: 'deposit' as TransferTab,
    isSelectingDepositDestination: false,
    isSelectingDepositSource: false,
};

function getOpenDepositState(fundingAccountStatus = getFundingAccountStatus()) {
    return {
        ...OPEN_DEPOSIT_STATE,
        ...(fundingAccountStatus === 'perpsOnly' ? { depositDestination: 'margined' as const } : {}),
    };
}

export const useTransferDialogStore = create<TransferDialogState>()((set) => ({
    isOpen: false,
    activeTab: 'deposit' as TransferTab,
    depositDestination: 'event_contract',
    isSelectingDepositDestination: false,
    isSelectingDepositSource: false,
    withdrawalSource: 'event_contract',
    isSelectingWithdrawalSource: false,
    depositAutoSelectFired: false,
    actions: {
        depositOpened: () => {
            if (!shouldOpenDeposit()) return false;
            const fundingAccountStatus = getFundingAccountStatus();
            set(getOpenDepositState(fundingAccountStatus));
            warmPlaidProtectSession(useAuthStore.getState().userId ?? '');
            return true;
        },
        depositOpenedUnchecked: () => {
            set(getOpenDepositState());
            warmPlaidProtectSession(useAuthStore.getState().userId ?? '');
        },
        withdrawOpened: () => {
            set({
                isOpen: true,
                activeTab: 'withdraw',
                isSelectingDepositDestination: false,
                isSelectingDepositSource: false,
                isSelectingWithdrawalSource: false,
            });
        },
        transferOpened: () => {
            set({
                isOpen: true,
                activeTab: 'transfer',
                isSelectingDepositDestination: false,
                isSelectingDepositSource: false,
                isSelectingWithdrawalSource: false,
            });
        },
        tabSwitched: (tab) => set({ activeTab: tab }),
        dialogClosed: () => {
            set({
                isOpen: false,
                activeTab: 'deposit',
                depositDestination: 'event_contract',
                isSelectingDepositDestination: false,
                isSelectingDepositSource: false,
                withdrawalSource: 'event_contract',
                isSelectingWithdrawalSource: false,
                depositAutoSelectFired: false,
            });
            invalidateAllBalanceQueries();
        },
        depositAutoSelectDidFire: () => set({ depositAutoSelectFired: true }),
        depositDestinationPickerOpened: () => set({ isSelectingDepositDestination: true }),
        depositDestinationPickerClosed: () => set({ isSelectingDepositDestination: false }),
        depositDestinationSelected: (depositDestination) =>
            set({
                depositDestination: getFundingAccountStatus() === 'perpsOnly' ? 'margined' : depositDestination,
            }),
        depositSourcePickerOpened: () => set({ isSelectingDepositSource: true }),
        depositSourcePickerClosed: () => set({ isSelectingDepositSource: false }),
        withdrawalSourcePickerOpened: () => set({ isSelectingWithdrawalSource: true }),
        withdrawalSourcePickerClosed: () => set({ isSelectingWithdrawalSource: false }),
        withdrawalSourceSelected: (withdrawalSource) => set({ withdrawalSource }),
    },
}));
